/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Button } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import { Workbook } from '@grapecity/wijmo.xlsx';
import { addClass, hasClass } from '@grapecity/wijmo';
import {
  DataMap,
  CellType,
  CellRange,
  CellRangeEventArgs,
  HeadersVisibility,
  GroupRow,
} from '@grapecity/wijmo.grid';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';
import {
  findProjectStepSetupStep,
  findProjectStepType,
  findSetupStep,
  findProjectCommonStep,
  saveProjectStepSetupStep,
} from 'apis/setup/SetupStep';
import { tabs } from 'components/layouts/Tab';
import CustomGrid, { CustomMergeManager } from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import { getExcelFileName } from 'utils/ExcelUtil';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import SetupStepAddRowPopup from './SetupStepAddRowPopup';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { CrudCode } from 'models/common/Edit';
import { ComboBox } from 'components/selects/ComboBox';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회 > 프로젝트 셋업단계 설정 > [셋업 단계] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 * 2026.08.07   jshwang12       설비가 등록된 경우 단계 수정 불가 (가중치 포함)
 * 2026.08.25   jshwang12       임시저장 기능, 자동계산단계/일반단계 행추가 방식 수정, 자동계산단계 트리구조로 변경
 * 2026.09.02   jshwang12       셋업단계 콤보박스는 이미 선택한 단계를 제외하고 노출
 */

interface Props {
  elmPrjId?: string;
  isReadOnly?: boolean; // 읽기만 가능여부 (true인 경우 수정권한이 아예 없는 상태)
  setupStatus?: SetupStep[]; // 단계유형별 설정가능 상태정보
  onRefreshStatus?: () => void; // 상태정보 재조회
}

const SetupStepContent = (
  { isReadOnly = true, setupStatus = [], onRefreshStatus, ...props }: Props,
  ref
) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<SetupStepCondition>();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [rowData, setRowData] = useState<Record<string, SetupStep[]>>({});
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const [spTpCds, setSpTpCds] = useState<SetupStep[]>([]);
  const [cmnPrjSpCds, setCmnPrjSpCds] = useState<SetupStep[]>([]);
  const [isOpenAddRowPopup, setOpenAddRowPopup] = useState<boolean>(false);
  const dragIndex = useRef<any>();
  const [istlDivsCd, setIstlDivsCd] = useState<string>();

  useImperativeHandle(ref, () => ({
    search: (params?: SetupStepCondition) => {
      setCondition(params);
      handleSearch(params?.elmPrjId);
    },
    resetTabIndex: () => {
      setCurrentTabIndex(0);
    },
    // 상위 컴포넌트의 설정완료 처리에서 사용
    hasChanges: () => hasChanges(),
    saveChanges: () => saveChanges(),
  }));

  useEffect(() => {
    onRefreshStatus?.();
  }, [currentTabIndex]);

  const groupByIstlDivsWithTotal = (data) => {
    const groups = {};
    // istlDivsCd별로 그룹화 및 합계 계산
    data.forEach((item) => {
      const key = item.istlDivsCd || '기타';
      if (!groups[key]) {
        groups[key] = {
          rowType: 'ROOT',
          istlDivsCd: key,
          istlDivsNm: item.istlDivsNm || key,
          children: [],
        };
      }
      // 이전에 묶였던 하위 항목 초기화 (재계산 시 중복 방지)
      delete item.children;
      groups[key].children.push(item);
    });

    // 그룹 배열로 변환
    const result = Object.values(groups) as any[];

    // 자동계산단계를 설정한 행은 계산대상 단계(자동계산단계)의 하위 항목으로 묶기
    result.forEach((group) => {
      const items = group.children as any[];
      const mapPrjSpCd = new Map(items.filter((o) => o.prjSpCd).map((o) => [o.prjSpCd, o]));
      group.children = items.filter((item) => {
        const parent = item.autoCalSpCd ? mapPrjSpCd.get(item.autoCalSpCd) : undefined;
        // 계산대상 단계가 동일한 셋업구분에 없는 경우 상위 레벨 유지
        if (!parent || parent === item) return true;
        parent.children = [...(parent.children || []), item];
        return false;
      });
    });

    return result;
  };

  const filterRowData = useMemo(() => {
    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    if (rowData && spTpCd) {
      if (Object.keys(rowData || {}).filter((key) => key === spTpCd)) {
        return groupByIstlDivsWithTotal(rowData[spTpCd] || []);
      }
    }
    return [];
  }, [currentTabIndex, rowData, spTpCds]);

  // 현재 탭 단계유형에 설비가 등록된 경우 셋업단계 수정 불가
  const readOnly = useMemo(() => {
    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    const status = (setupStatus || []).find((o) => o.spTpCd === spTpCd);
    if (!status) return true;
    return isReadOnly || status.eqpYn !== 'N';
  }, [isReadOnly, setupStatus, spTpCds, currentTabIndex]);

  // 단계설정 완료여부 (완료된 경우 임시저장 불가)
  const setgCpltYn = useMemo(() => {
    return setupStatus?.[0]?.setgCpltYn || 'Y';
  }, [setupStatus]);

  const applyRowReadOnly = useEvent((grid) => {
    if (!grid) return;
    // 전체 행 편집모드 활성화 후 특정 컬럼 readonly 처리는 itemFormatter에서 처리
    grid.rows.forEach((row) => {
      row.isReadOnly = readOnly;
    });
  });

  /**
   * 행 로드 완료
   */
  const onLoadedRows = useEvent((grid, e) => {
    applyRowReadOnly(grid);
    updateFooter(grid);
  });

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = readOnly
        ? HeadersVisibility.Column
        : HeadersVisibility.All;
      // 수정권한이 뒤늦게 확정되는 경우를 위해 전체 행에 재적용
      applyRowReadOnly(gridRef.current);
      gridRef.current.invalidate();
    }
  }, [readOnly, filterRowData]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 셋업구분
      findSetupStep({ useYn: 'Y' }), // 전체 단계목록
    ]).then(([prjSpDivsCd, prjSpCd]) => {
      setCode({
        prjSpDivsCd: prjSpDivsCd,
      });
      setPrjSpCds(prjSpCd);
    });
  };

  const handleSearch = async (elmPrjId) => {
    if (elmPrjId) {
      Promise.all([
        findProjectStepType(elmPrjId), // 단계유형
        findProjectStepSetupStep({ elmPrjId }), // 유형별 단계 목록
        findProjectCommonStep({ elmPrjId }), // 공통단계목록
      ]).then(([spTpCd, steps, cmnSteps]) => {
        setSpTpCds(spTpCd);
        setRowData(steps);
        setCmnPrjSpCds(cmnSteps);
      });
    } else {
      setSpTpCds([]);
      setRowData({});
      setCmnPrjSpCds([]);
    }
  };

  const canReorder = useEvent((newItems, prevPrjSpCds) => {
    const curCmnSteps = newItems.filter((o) => o.cmnYn === 'Y');
    const curPrjSpCds = curCmnSteps.map((o) => o.prjSpCd);
    const originalCmnOrder = (cmnPrjSpCds || []).map((o) => o.prjSpCd);

    // 1. 기준 순서에 없는 코드가 끼어있는지 확인
    const hasInvalidCode = curPrjSpCds.some((cd) => !originalCmnOrder.includes(cd));
    if (hasInvalidCode) return false;

    // 2. 기준 순서의 모든 코드가 포함되어 있는지 확인
    const hasAllOriginalCodes = originalCmnOrder.every((cd) => curPrjSpCds.includes(cd));
    if (!hasAllOriginalCodes) return false;

    // 공통단계 순서에 변화가 없는 경우
    const prevStr = JSON.stringify(prevPrjSpCds);
    const curStr = JSON.stringify(curPrjSpCds);
    if (prevStr === curStr) return true;

    const getInversionCount = (arr) => {
      let count = 0;
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          // 기준 순서에서 앞에 있어야 할 요소가 뒤에 있는 경우 (Inversion)
          if (originalCmnOrder.indexOf(arr[i]) > originalCmnOrder.indexOf(arr[j])) {
            count++;
          }
        }
      }
      return count;
    };

    const prevInversions = getInversionCount(prevPrjSpCds);
    const curInversions = getInversionCount(curPrjSpCds);

    return curInversions <= prevInversions;
  });

  /**
   * 현재 탭 단계유형의 셋업단계 목록
   */
  const currentRows = useMemo(() => {
    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    return (spTpCd ? rowData?.[spTpCd] : []) || [];
  }, [rowData, spTpCds, currentTabIndex]);

  /**
   * 자동계산단계 여부
   * @param item
   */
  const isAutoCalSp = useEvent((item) => {
    if (readOnly) {
      // 읽기모드: 조회된 자동계산여부가 Y인 경우 true
      return item?.autoCalYn === 'Y';
    }
    // 편집모드: 다른 행의 자동계산단계로 참조되는 경우 true
    if (!item?.prjSpCd) {
      return false;
    }
    // 삭제 대상 행은 제외 (하위 행이 모두 삭제된 경우 자동계산단계가 아니므로 일반 행과 동일하게 계산)
    return currentRows.some((o) => o.crudKey !== CrudCode.DELETE && o.autoCalSpCd === item.prjSpCd);
  });

  /**
   * 셋업단계 콤보박스 선택 목록
   * [26.09.02] 선택한 셋업구분에 해당하는 단계 중 그리드에 이미 추가된 단계는 제외 (저장 전 항목 포함)
   * @param item
   */
  const getSelectablePrjSpNms = useEvent((item) => {
    // 그리드에 노출중인 단계 (자기 자신은 현재 값 표시를 위해, 삭제 대상은 재선택 가능하도록 제외)
    const addedPrjSpCds = currentRows
      .filter((o) => o !== item && o.crudKey !== CrudCode.DELETE)
      .map((o) => o.prjSpCd)
      .filter((o) => !!o);

    return (prjSpCds || [])
      .filter((o) => o.istlDivsCd === item.istlDivsCd && !addedPrjSpCds.includes(o.prjSpCd))
      .map((o) => o.prjSpNm || '');
  });

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링 (공통단계 제외)
    mapPrjSpCd.getDisplayValues = (item) => getSelectablePrjSpNms(item);

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: readOnly,
        cellTemplate: (params) => {
          if (params.item.rowType === 'ROOT') {
            return '';
          }
          // total, root 제외한 행번호
          let count = 0;
          for (let i = 0; i < params.row._idx; i++) {
            const rowItem = gridRef.current?.rows?.[i]?.dataItem;
            if (rowItem?.rowType !== 'ROOT') {
              count++;
            }
          }
          return count + 1;
        },
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: !readOnly,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서', '순서')),
        minWidth: 43,
        width: 43,
        visible: !readOnly,
        isReadOnly: true,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
      },
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.셋업 구분', '셋업 구분')),
        align: 'center',
        minWidth: 80,
        width: 150,
        isReadOnly: true,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계 명', '셋업 단계 명')),
        align: 'left',
        minWidth: 40,
        width: '*',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapPrjSpCd,
      },
      {
        binding: 'autoCalSpCd',
        header: String(t('setup.label.자동 계산 단계', '자동 계산 단계')),
        align: 'left',
        minWidth: 40,
        width: '*',
        isRequired: false,
        // 공통단계 선택 후 행추가한 경우에만 지정되며 직접 수정 불가
        isReadOnly: true,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm'),
      },
      {
        binding: 'divsWghtScor',
        header: String(t('setup.label.자동계산\n가중치', '자동계산\n가중치')),
        align: 'center',
        minWidth: 40,
        width: 150,
        isRequired: false,
      },
      {
        binding: 'wghtScor',
        header: String(t('setup.label.단계별 가중치', '단계별 가중치')),
        align: 'center',
        minWidth: 40,
        width: 150,
        isRequired: false,
        cellTemplate: (params) => {
          /*if (params.item.rowType === 'TOTAL') {
            return (gridRef.current?.rows || []).reduce(
              (acc, cur) => acc + (cur.dataItem.wghtScor ? Number(cur.dataItem.wghtScor) : 0),
              0
            );
          }*/
          return params.value || '';
        },
      },
      {
        binding: 'cvcgScor',
        header: String(t('setup.label.환산 가중치\n(설비기준)', '환산 가중치\n(설비기준)')),
        align: 'center',
        minWidth: 40,
        width: 150,
        isRequired: false,
        isReadOnly: true,
        cellTemplate: (params) => getCvcgScor(params.item),
      },
    ];
  }, [code, prjSpCds, readOnly, currentTabIndex, spTpCds]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (binding) {
        // 공통단계 여부
        if (item.cmnYn === 'Y') {
          addClass(cell, 'cmnStepRow');
        }
        // 자동계산단계가 설정된 행 여부
        else if (item.autoCalSpCd) {
          addClass(cell, 'autoCalRow');
        }
      }
      if (readOnly) {
        cell.ariaReadOnly = true;
      } else {
        // 셋업단계
        if ('prjSpCd' === binding) {
          // 공통단계인 경우 수정불가
          if (item.cmnYn === 'Y') {
            cell.ariaReadOnly = true;
            if (cell.firstElementChild instanceof HTMLButtonElement) {
              cell.firstChild.remove();
            }
          }
        }
        // 자동계산단계 (공통단계 선택 후 행추가한 경우에만 지정되며 직접 수정 불가)
        if ('autoCalSpCd' === binding) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
        // 자동계산 가중치
        if ('divsWghtScor' === binding) {
          cell.ariaReadOnly = !item?.autoCalSpCd; // 자동계산단계를 선택한 경우에만 편집가능
        }
        // 단계별 가중치
        if ('wghtScor' === binding) {
          cell.ariaReadOnly = !!item?.autoCalSpCd; // 자동계산단계를 미선택한 경우에만 편집가능
        }
      }
      if (item?.rowType === 'ROOT') {
        cell.ariaReadOnly = true;
        if (['prjSpCd', 'autoCalSpCd'].includes(binding)) {
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  /**
   * 행추가
   * 공통단계 행을 선택한 경우: 선택한 공통단계를 자동계산단계로 지정한 하위 행으로 추가
   * 그 외의 경우: 선택한 단계유형(셋업구분) 그룹의 마지막 행 뒤에 추가
   */
  const handleAddRow = () => {
    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    if (!rowData || !spTpCd) return;

    // 선택된 공통단계 행 (여러 행 선택 시 첫번째 공통단계 행 기준)
    const selectedCmnRow = (gridRef.current?.rows || [])
      .filter((r) => r.isSelected)
      .map((r) => r.dataItem)
      .find((o) => o?.rowType !== 'ROOT' && o?.cmnYn === 'Y');

    // 공통단계 선택 시에는 해당 행의 셋업구분을 따름
    const newIstlDivsCd = selectedCmnRow?.istlDivsCd || istlDivsCd;
    if (!newIstlDivsCd) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.추가할 단계유형을 선택해 주세요.',
          '추가할 단계유형을 선택해 주세요.'
        ),
      });
      return;
    }

    const uRowData = { ...rowData };
    const currentItems = [...(uRowData[spTpCd] || [])];

    const newRow = {
      crudKey: CrudCode.CREATE,
      spTpCd: spTpCd,
      prjSpCd: '',
      prjSpNm: '',
      istlDivsCd: newIstlDivsCd,
      cmnYn: 'N',
      autoCalYn: '',
      // 공통단계 선택 시 해당 공통단계를 자동계산단계로 지정
      autoCalSpCd: selectedCmnRow?.prjSpCd || '',
      divsWghtScor: '',
      wghtScor: '',
      cvcgScor: '',
    };

    // 신규행을 삽입할 위치 찾기
    let insertIdx = -1;
    if (selectedCmnRow) {
      // 선택한 공통단계 행의 인덱스 (참조로 찾지 못한 경우 셋업단계 코드로 찾기)
      insertIdx = currentItems.findIndex((o) => o === selectedCmnRow);
      if (insertIdx === -1) {
        insertIdx = currentItems.findIndex((o) => o.prjSpCd === selectedCmnRow.prjSpCd);
      }
      // 이미 추가된 하위 행이 있는 경우 마지막 하위 행 뒤에 삽입
      if (insertIdx !== -1) {
        for (let i = insertIdx + 1; i < currentItems.length; i++) {
          if (currentItems[i].autoCalSpCd !== selectedCmnRow.prjSpCd) break;
          insertIdx = i;
        }
      }
    } else {
      // 동일한 istlDivsCd를 가진 마지막 행의 인덱스 찾기
      for (let i = currentItems.length - 1; i >= 0; i--) {
        if (currentItems[i].istlDivsCd === newIstlDivsCd) {
          insertIdx = i;
          break;
        }
      }
    }

    // 대상 행이 존재하면 해당 행 바로 뒤에 삽입, 없으면 맨 끝에 추가
    const newItems =
      insertIdx === -1
        ? [...currentItems, newRow]
        : [...currentItems.slice(0, insertIdx + 1), newRow, ...currentItems.slice(insertIdx + 1)];

    setRowData((prev) => ({
      ...prev,
      [spTpCd]: newItems,
    }));
    // setOpenAddRowPopup(true);
  };

  const handleDelRow = () => {
    const grid = gridRef.current;
    if (!grid) return;

    const selectedRows = grid.rows.filter((r) => r.isSelected);
    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 대상을 선택해 주세요.', '삭제할 대상을 선택해 주세요.'),
      });
      return;
    }

    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    if (!rowData || !spTpCd) return;

    const currentData = [...rowData[spTpCd]];
    const itemsToDelete: any[] = [];
    const groupsToDelete: string[] = [];

    // 선택된 행들을 수집
    selectedRows.forEach((row) => {
      const item = row.dataItem;

      // 그룹 헤더(ROOT) 행인 경우
      if (item.rowType === 'ROOT') {
        const groupKey = item.istlDivsCd;
        groupsToDelete.push(groupKey);
        return;
      }
      itemsToDelete.push(item);
    });

    // 삭제할 그룹이 있는 경우
    if (groupsToDelete.length > 0) {
      groupsToDelete.forEach((groupKey) => {
        // 해당 그룹의 모든 아이템을 삭제 목록에 추가
        const groupItems = currentData.filter((item) => item.istlDivsCd === groupKey);
        itemsToDelete.push(...groupItems);
      });
    }

    if (itemsToDelete.length === 0) {
      return;
    }

    // 중복 제거
    const uniqueItemsToDelete = Array.from(new Set(itemsToDelete));

    // 삭제할 아이템들을 처리
    uniqueItemsToDelete.forEach((item) => {
      // 원본 데이터에서 해당 아이템 찾기 (참조 비교)
      let index = currentData.findIndex((dataItem) => dataItem === item);

      // 참조로 찾지 못한 경우, 고유 ID로 찾기
      if (index === -1) {
        index = currentData.findIndex((dataItem) => dataItem.prjSpCd === item.prjSpCd);
      }

      if (index !== -1) {
        if (currentData[index].crudKey === CrudCode.CREATE) {
          currentData.splice(index, 1);
        } else {
          //  공통단계의 경우 삭제 불가
          if (currentData[index].cmnYn !== 'Y') {
            currentData[index].crudKey = CrudCode.DELETE;
          }
        }
      }
    });

    // 원본 데이터 업데이트
    setRowData((prev) => ({
      ...prev,
      [spTpCd]: currentData,
    }));

    // 그리드 선택 해제 (삭제된 행의 선택 상태가 남아 이후 행추가/행삭제에 영향을 주지 않도록)
    grid.rows.forEach((row) => {
      row.isSelected = false;
    });
  };

  /**
   * 데이터 검증
   * @param rows
   * @param isDraft  임시저장 여부 (true인 경우 가중치 관련 검증 안함)
   * @returns
   */
  const validate = (rows, isDraft = false) => {
    const seen = new Map<string, number>();
    const dupKeys = [] as string[];
    // 삭제 대상 행은 제외 (삭제한 셋업단계는 다시 추가할 수 있으므로 중복으로 보지 않음)
    (rows || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE && o.prjSpCd)
      .forEach((o) => {
        const key = `${o.spTpCd}|${o.prjSpCd}`;
        const count = seen.get(key) || 0;
        seen.set(key, count + 1);
        if (count === 1) {
          dupKeys.push(key);
        }
      });
    const valid = (rows || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o) => {
        const spTpNm = spTpCds.find((s) => s.spTpCd === o.spTpCd)?.spTpNm || o.spTpCd;
        const prjSpNm = prjSpCds.find((p) => p.prjSpCd === o.prjSpCd)?.prjSpNm || o.prjSpCd;
        const tabPrjSpNm = `[${spTpNm}][${prjSpNm}]`;
        if (_.isEmpty(o.istlDivsCd)) {
          return `[${spTpNm}] : ${t('setup.label.셋업 구분이 없습니다.', '셋업 구분이 없습니다.')}`;
        }
        if (_.isEmpty(o.prjSpCd)) {
          return `[${spTpNm}] : ${t(
            'setup.label.셋업 단계를 선택해 주세요.',
            '셋업 단계를 선택해 주세요.'
          )}`;
        }
        const key = `${o.spTpCd}|${o.prjSpCd}`;
        if (dupKeys.includes(key || '')) {
          return `${tabPrjSpNm} : ${t(
            'setup.label.중복된 셋업 단계 코드가 있습니다.',
            '중복된 셋업 단계 코드가 있습니다.'
          )}`;
        }
        // 임시저장인 경우 가중치 관련 검증안함
        if (!isDraft) {
          // 자동계산 단계가 설정되어 있는 경우
          if (o.autoCalSpCd) {
            // 자동계산 가중치 확인
            if (_.isEmpty(o.divsWghtScor)) {
              return `${tabPrjSpNm} : ${t(
                'setup.label.자동계산 가중치를 입력해 주세요.',
                '자동계산 가중치를 입력해 주세요.'
              )}`;
            }
            if (_.isNaN(Number(o.divsWghtScor))) {
              return `${tabPrjSpNm} : ${t(
                'setup.label.자동계산 가중치 값을 확인해 주세요.',
                '자동계산 가중치 값을 확인해 주세요.'
              )}`;
            }
            // 설정한 자동계산단계가 셋업단계에 포함되어있는지 확인
            const autoCd = rows.find(
              (r) => r.spTpCd === o.spTpCd && r.prjSpCd === o.autoCalSpCd
            )?.prjSpCd;
            if (!autoCd) {
              const autoPrjSpNm =
                prjSpCds.find((p) => p.prjSpCd === o.autoCalSpCd)?.prjSpNm || o.autoCalSpCd;
              return `[${spTpNm}][${autoPrjSpNm}] : ${t(
                'setup.label.자동계산단계가 셋업단계에 포함되어 있지 않습니다.',
                '자동계산단계가 셋업단계에 포함되어 있지 않습니다.'
              )}`;
            }
          }
          // 자동계산단계가 설정안된경우
          else {
            // 단계별 가중치 확인
            if (_.isEmpty(o.wghtScor)) {
              return `${tabPrjSpNm} : ${t(
                'setup.label.단계별 가중치를 입력해 주세요.',
                '단계별 가중치를 입력해 주세요.'
              )}`;
            }
            if (_.isNaN(Number(o.wghtScor))) {
              return `${tabPrjSpNm} : ${t(
                'setup.label.단계별 가중치 값을 확인해 주세요.',
                '단계별 가중치 값을 확인해 주세요.'
              )}`;
            }
          }
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    // 임시저장인 경우 가중치 관련 검증안함
    if (!isDraft) {
      // 단계별 가중치 합산 확인
      const spTpCdSet = new Set(
        rows.filter((o) => o.crudKey !== CrudCode.DELETE).map((o) => o.spTpCd)
      );
      const weightSumErrors = [] as string[];

      spTpCdSet.forEach((spTpCd) => {
        const targetRows = rows.filter((o) => o.spTpCd === spTpCd && o.crudKey !== CrudCode.DELETE);

        // 단계별 가중치 합산
        const wghtSum = targetRows.reduce((acc, cur) => {
          return acc + (Number(cur.wghtScor) || 0);
        }, 0);

        if (Math.round((wghtSum + Number.EPSILON) * 100) / 100 !== 100) {
          const spTpNm = spTpCds.find((s) => s.spTpCd === spTpCd)?.spTpNm || spTpCd;
          weightSumErrors.push(
            `[${spTpNm}] : ${t(
              'setup.label.단계별 가중치 합이 100이 아닙니다.',
              '단계별 가중치 합이 100이 아닙니다.'
            )}`
          );
        }

        // 환산 가중치 합산
        const cvcgSum = targetRows.reduce((acc, cur) => {
          // 자동계산단계에 포함된 행인지 확인 (다른 행의 autoCalSpCd가 이 행의 prjSpCd와 같은 경우)
          const isAutoCalTarget = targetRows.some((o) => o.autoCalSpCd === cur.prjSpCd);
          if (isAutoCalTarget) {
            return acc; // 자동계산단계에 포함된 행은 환산 가중치 0
          }
          // 자동계산단계를 설정한 행
          if (cur.autoCalSpCd) {
            const autoSpCd = targetRows.find((o) => o.prjSpCd === cur.autoCalSpCd);
            const cvcgScor =
              ((Number(cur.divsWghtScor) || 0) * (Number(autoSpCd?.wghtScor) || 0)) / 100;
            return acc + cvcgScor;
          }
          // 일반 행
          return acc + (Number(cur.wghtScor) || 0);
        }, 0);

        if (Math.round((cvcgSum + Number.EPSILON) * 100) / 100 !== 100) {
          const spTpNm = spTpCds.find((s) => s.spTpCd === spTpCd)?.spTpNm || spTpCd;
          weightSumErrors.push(
            `[${spTpNm}] : ${t(
              'setup.label.환산 가중치 합이 100이 아닙니다.',
              '환산 가중치 합이 100이 아닙니다.'
            )}`
          );
        }
      });

      if (weightSumErrors.length) {
        openMessageBar({ type: 'error', content: weightSumErrors.join('<br/>') });
        return false;
      }
    }
    return true;
  };

  /**
   * 저장 순서 정렬
   * 화면 노출 순서(셋업구분 그룹 -> 그룹 내 순서)를 유지하되,
   * 자동계산단계(계산대상)만 해당 단계를 자동계산단계로 참조하는 하위 행들보다 뒤로 이동
   * (화면은 부모 -> 하위 순으로 노출되나 저장은 하위 -> 부모 순서로 처리)
   */
  const sortForSave = (items: any[]) => {
    // 화면과 동일한 기준으로 셋업구분 그룹화 (groupByIstlDivsWithTotal와 동일)
    const groupKeys: string[] = [];
    const groups: Record<string, any[]> = {};
    (items || []).forEach((item) => {
      const key = item.istlDivsCd || '기타';
      if (!groups[key]) {
        groups[key] = [];
        groupKeys.push(key);
      }
      groups[key].push(item);
    });

    const result: any[] = [];
    groupKeys.forEach((key) => {
      const groupItems = groups[key];
      const mapPrjSpCd = new Map(groupItems.filter((o) => o.prjSpCd).map((o) => [o.prjSpCd, o]));

      // 자동계산단계의 하위로 묶이는 행 (화면에서 상위 레벨에 노출되지 않는 행)
      const childItems = new Map<any, any[]>();
      const nestedItems = new Set<any>();
      groupItems.forEach((item) => {
        const parent = item.autoCalSpCd ? mapPrjSpCd.get(item.autoCalSpCd) : undefined;
        if (!parent || parent === item) return;
        childItems.set(parent, [...(childItems.get(parent) || []), item]);
        nestedItems.add(item);
      });

      // 상위 레벨 행을 화면 순서대로 순회하며 하위 행을 먼저 배치
      groupItems.forEach((item) => {
        if (nestedItems.has(item)) return;
        (childItems.get(item) || []).forEach((child) => result.push(child));
        result.push(item);
      });
    });

    return result;
  };

  const convertSaveRows = () => {
    // children은 트리 노출용 항목이므로 저장 대상에서 제외
    const rows: SetupStep[] = Object.entries(rowData).flatMap(([spTpCd, items]) => {
      const sortedItems = sortForSave(items as any[]);
      // 다른 행의 자동계산단계로 참조되는 단계 (삭제 대상 행의 참조는 제외)
      const autoCalSpCds = new Set(
        sortedItems
          .filter((o) => o.crudKey !== CrudCode.DELETE && o.autoCalSpCd)
          .map((o) => o.autoCalSpCd)
      );

      return sortedItems.map(({ children, ...item }: any) => {
        // 자동계산단계에 포함된 단계 여부
        const autoCalYn = item.prjSpCd && autoCalSpCds.has(item.prjSpCd) ? 'Y' : 'N';
        return {
          ...item,
          spTpCd: spTpCd,
          elmPrjId: item.elmPrjId || condition?.elmPrjId,
          autoCalYn: autoCalYn,
          // 자동계산여부만 변경된 경우에도 저장되도록 상태 처리
          crudKey:
            !item.crudKey && (item.autoCalYn || 'N') !== autoCalYn ? CrudCode.UPDATE : item.crudKey,
        };
      });
    });
    return rows;
  };

  /**
   * 임시저장
   */
  const handleDraft = () => {
    const rows: SetupStep[] = convertSaveRows();
    if (validate(rows, true)) {
      if (rows.findIndex((o) => !!o.crudKey) < 0) {
        return;
      }
      saveProjectStepSetupStep(rows).then((res) => {
        openMessageBar({
          type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
          content:
            res?.successOrNot === 'Y'
              ? t('com.label.저장되었습니다.', '저장되었습니다.')
              : res.data && typeof res.data === 'string'
              ? res.data
              : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
        if (res?.successOrNot === 'Y') {
          handleSearch(condition?.elmPrjId);
        }
      });
    }
  };

  /**
   * 전체 탭의 공통단계 순서 검증
   */
  const validateCommonStepOrder = () => {
    const originalCmnOrder = (cmnPrjSpCds || []).map((o) => o.prjSpCd);
    const orderErrors = [] as string[];

    Object.entries(rowData).forEach(([spTpCd, items]) => {
      const spTpNm = spTpCds.find((s) => s.spTpCd === spTpCd)?.spTpNm || spTpCd;
      const curPrjSpCds = (items || []).filter((o) => o.cmnYn === 'Y').map((o) => o.prjSpCd);
      // 공통단계가 존재하는 경우에만 검증
      if (originalCmnOrder.length > 0 || curPrjSpCds.length > 0) {
        const isOrderMatched =
          originalCmnOrder.length === curPrjSpCds.length &&
          originalCmnOrder.every((cd, index) => cd === curPrjSpCds[index]);

        if (!isOrderMatched) {
          orderErrors.push(
            `[${spTpNm}] : ${t(
              'setup.label.공통단계의 순서가 올바르지 않습니다.',
              '공통단계의 순서가 올바르지 않습니다.'
            )}`
          );
        }
      }
    });

    if (orderErrors.length > 0) {
      openMessageBar({ type: 'error', content: orderErrors.join('<br/>') });
      return false;
    }
    return true;
  };

  /**
   * 변경사항 존재여부 (상위 컴포넌트의 설정완료 처리에서 사용)
   */
  const hasChanges = () => {
    return convertSaveRows().findIndex((o) => !!o.crudKey) > -1;
  };

  /**
   * 검증 후 저장 (확인 모달 없이 처리하며 성공여부를 반환)
   * 상위 컴포넌트의 설정완료 처리에서 사용
   */
  const saveChanges = async () => {
    if (!validateCommonStepOrder()) {
      return false;
    }
    const rows: SetupStep[] = convertSaveRows();
    if (!validate(rows)) {
      return false;
    }
    if (rows.findIndex((o) => !!o.crudKey) < 0) {
      return true;
    }
    const res = await saveProjectStepSetupStep(rows);
    if (res?.successOrNot !== 'Y') {
      openMessageBar({
        type: 'error',
        content:
          res.data && typeof res.data === 'string'
            ? res.data
            : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
      });
      return false;
    }
    handleSearch(condition?.elmPrjId);
    return true;
  };

  const handleSave = () => {
    // 저장 전 전체 탭의 공통단계 순서 최종 검증
    if (!validateCommonStepOrder()) {
      return;
    }

    const rows: SetupStep[] = convertSaveRows();
    if (validate(rows)) {
      if (rows.findIndex((o) => !!o.crudKey) < 0) {
        return;
      }
      openCommonModal({
        modalType: 'confirm',
        content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
        yesCallback: async () => {
          saveProjectStepSetupStep(rows).then((res) => {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : res.data && typeof res.data === 'string'
                  ? res.data
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            if (res?.successOrNot === 'Y') {
              handleSearch(condition?.elmPrjId);
            }
          });
        },
      });
    }
  };

  const handleDownloadExcel = async () => {
    const workbook = new Workbook();
    for (const spTpCd of spTpCds || []) {
      if (spTpCd.spTpCd) {
        const ws = CommonUtil.createWijmoSheet(
          spTpCd.spTpNm || spTpCd.spTpCd,
          layoutDefinition,
          rowData[spTpCd.spTpCd]
        );
        workbook.sheets.push(ws);
      }
    }
    workbook.saveAsync(getExcelFileName(t('setup.label.셋업 단계', '셋업 단계')));
  };

  const onBeginningEdit = useEvent((grid, e) => {
    if (readOnly) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;

      if (item?.rowType === 'ROOT') {
        e.cancel = true;
      }
      // 셋업단계
      if ('prjSpCd' === binding) {
        // 공통단계인 경우 수정불가
        if (item.cmnYn === 'Y') {
          e.cancel = true;
        }
      }
      // 자동계산단계 (공통단계 선택 후 행추가한 경우에만 지정되며 직접 수정 불가)
      if ('autoCalSpCd' === binding) {
        e.cancel = true;
      }
      // 자동계산 가중치
      if ('divsWghtScor' === binding) {
        if (!item?.autoCalSpCd) {
          e.cancel = true; // 자동계산단계를 선택한 경우에만 편집가능
        }
      }
      // 단계별 가중치
      if ('wghtScor' === binding) {
        if (item?.autoCalSpCd) {
          e.cancel = true; // 자동계산단계를 미선택한 경우에만 편집가능
        }
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const newVal = grid.activeEditor?.value;

    // 자동계산 가중치
    if ('divsWghtScor' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
    // 단계별 가중치
    if ('wghtScor' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    updateFooter(grid);
  });

  const onInitialized = useEvent((grid) => {
    grid.autoRowHeights = true;
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.headersVisibility = readOnly ? HeadersVisibility.Column : HeadersVisibility.All;
    grid.mergeManager = new ProjectMergeManager();
    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel === s.columnHeaders) {
        e.cell.style.display = 'flex';
        e.cell.style.alignItems = 'center';
        e.cell.style.justifyContent = 'center';
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      // 트리 부모 노드의 체크박스는 하위 행의 선택 상태로 파생되므로 행 자체의 선택 상태로 보정
      if (e.panel === s.rowHeaders) {
        const input = e.cell.querySelector('input');
        if (input) {
          input.checked = !!s.rows[e.row]?.isSelected;
          input.indeterminate = false;
        }
      }
      /*if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (item?.usedYn === 'Y' || item?.cmnYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }*/
      if (e.panel === s.columnFooters) {
        if (e.row === 0 && e.col === 0) {
          e.cell.textContent = String(t('setup.label.합계', '합계'));
        }
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          /*if (item.cmnYn !== 'Y') {
            e.cell.draggable = true;
          } else {
            removeClass(e.cell, 'WijmoCellMove');
          }*/
          e.cell.draggable = true;
        }
        if ('crudKey' === binding || 'no' === binding) {
          if (!Object.prototype.hasOwnProperty.call(item, 'rowType') || !!item?.rowType) {
            e.cell.style.paddingLeft = null;
          }
        }
        // 접기/펼치기 버튼이 있는 행(하위 항목 보유)은 cellTemplate이 적용되지 않아
        // 상태값(crudKey)이 그대로 노출되므로 텍스트만 제거 (접기 버튼은 유지)
        if ('crudKey' === binding && item?.children?.length) {
          Array.from<ChildNode>(e.cell.childNodes).forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              node.remove();
            }
          });
        }
        // 하위 항목을 가진 행은 cellTemplate이 적용되지 않아 원시값이 노출되므로 직접 반영
        if (item?.rowType !== 'ROOT' && item?.children?.length) {
          // 자동계산단계(계산대상) 행에는 하위 행의 자동계산 가중치 합계 노출
          if ('divsWghtScor' === binding) {
            e.cell.textContent = String(getSubDivsWghtScorSum(item));
          }
          if ('cvcgScor' === binding) {
            e.cell.textContent = String(getCvcgScor(item));
          }
        }
        // 셋업구분(ROOT) 행에는 그룹별 가중치 합계 노출
        if (item?.rowType === 'ROOT' && ['wghtScor', 'cvcgScor'].includes(binding)) {
          e.cell.textContent = String(getGroupScorSum(item, binding));
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        /*grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.cmnYn === 'Y') {
              row.isSelected = false;
            }
          });
        });*/
      }
    });

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        const item = grid.rows[hti.row].dataItem;
        // 공통단계 드래그 불가
        /* if (item.cmnYn === 'Y') {
          e.preventDefault();
          return;
        }*/
        grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
      }
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
      }
    });
  });

  const moveItemInGroup = (items: any[], dragItem: any, targetItem: any): any[] => {
    const newItems = [...items];
    // 셋업단계를 선택하지 않은 신규행은 prjSpCd가 비어있어 중복될 수 있으므로 객체 참조로 비교
    const dragIdx = newItems.findIndex((item) => item === dragItem);
    const targetIdx = newItems.findIndex((item) => item === targetItem);

    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) {
      return newItems;
    }
    // 단계유형 그룹 내 위치 재배치
    newItems.splice(dragIdx, 1);
    newItems.splice(targetIdx, 0, dragItem);

    return newItems;
  };

  // 그룹 간 순서 변경 함수
  const moveGroupOrder = (
    originalItems: any[],
    dragGroupKey: string,
    targetGroupKey: string
  ): any[] => {
    // 그룹별로 아이템들을 분리
    const groups: { [key: string]: any[] } = {};
    const groupOrder: string[] = [];

    originalItems.forEach((item) => {
      const key = item.istlDivsCd || '기타';
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push(item);
    });

    // 그룹 순서 변경
    const dragIdx = groupOrder.indexOf(dragGroupKey);
    const targetIdx = groupOrder.indexOf(targetGroupKey);

    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) {
      return originalItems;
    }

    groupOrder.splice(dragIdx, 1);
    groupOrder.splice(targetIdx, 0, dragGroupKey);

    // 새로운 그룹 순서대로 아이템들을 재배치
    const result: any[] = [];
    groupOrder.forEach((key) => {
      result.push(...groups[key]);
    });

    return result;
  };

  // CRUD 상태 업데이트 함수
  const updateCrudKeys = (items: any[], groupKey: string): any[] => {
    return items.map((item) => {
      if (item.istlDivsCd === groupKey && item.crudKey !== CrudCode.CREATE) {
        return {
          ...item,
          crudKey: CrudCode.UPDATE,
        };
      }
      return item;
    });
  };

  // 원본 플랫 데이터에서 순서 업데이트 함수 (수정됨: slice 대신 map을 사용하여 안전하게 치환)
  const updateOriginalDataOrder = (
    originalItems: any[],
    groupKey: string,
    newChildren: any[]
  ): any[] => {
    // newChildren의 순서대로 원본 배열의 해당 그룹 아이템들을 치환하기 위한 큐(Queue) 생성
    const replaceQueue = [...newChildren];

    // 원본 배열을 순회하며 해당 그룹의 아이템인 경우에만 newChildren의 순서로 교체
    const result = originalItems.map((item) => {
      if (item.istlDivsCd === groupKey) {
        return replaceQueue.shift() || item; // 순서대로 꺼내서 치환
      }
      return item; // 다른 그룹 아이템은 그대로 유지
    });

    return result;
  };

  const onDraggedRow = useEvent((grid, e) => {
    const dragInfo = dragIndex.current;
    const targetItem = grid.rows[e.row].dataItem;

    if (!dragInfo || !targetItem) return;

    // 위치 변경 안된 경우
    if (dragInfo.item === targetItem) return;

    const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
    if (!rowData || !spTpCd) return;

    // 셋업 구분(ROOT) 간 이동인 경우
    if (dragInfo.item?.rowType === 'ROOT' && targetItem.rowType === 'ROOT') {
      const dragGroupKey = dragInfo.istlDivsCd;
      const targetGroupKey = targetItem.istlDivsCd;

      // 드래그 전의 공통단계 순서 추출
      const prevCmnSteps = (rowData[spTpCd] || []).filter((o) => o.cmnYn === 'Y');
      const prevPrjSpCds = prevCmnSteps.map((o) => o.prjSpCd);

      // 그룹 간 순서 변경
      let updatedOriginalData = moveGroupOrder(rowData[spTpCd], dragGroupKey, targetGroupKey);
      updatedOriginalData = updateCrudKeys(updatedOriginalData, dragGroupKey);

      if (!canReorder(updatedOriginalData, prevPrjSpCds)) {
        // if (isChangedCommonStepOrder(updatedOriginalData)) {
        openMessageBar({
          type: 'warning',
          content: String(
            t(
              'setup.label.공통단계의 순서는 변경할 수 없습니다.',
              '공통단계의 순서는 변경할 수 없습니다.'
            )
          ),
        });
        return;
      }

      setRowData((prev) => ({
        ...prev,
        [spTpCd]: updatedOriginalData,
      }));
      return;
    }

    // 셋업 구분 내 행 이동인 경우
    if (dragInfo.item?.rowType !== 'ROOT' && targetItem.rowType !== 'ROOT') {
      // 같은 그룹(istlDivsCd) 내에서만 이동 허용
      if (dragInfo.istlDivsCd !== targetItem.istlDivsCd) {
        openMessageBar({
          type: 'warning',
          content: String(
            t(
              'setup.label.같은 그룹 내에서만 순서 변경이 가능합니다.',
              '같은 그룹 내에서만 순서 변경이 가능합니다.'
            )
          ),
        });
        // 유효하지 않은 드래그인 경우 원본 데이터로 리렌더링하여 그리드 원복
        setRowData((prev) => ({ ...prev }));
        return;
      }

      // 원본 데이터에서 해당 그룹의 아이템들 추출
      const items = rowData[spTpCd] || [];
      const groupKey = dragInfo.istlDivsCd;

      // 드래그 전의 공통단계 순서 추출
      const prevCmnSteps = items.filter((o) => o.cmnYn === 'Y');
      const prevPrjSpCds = prevCmnSteps.map((o) => o.prjSpCd);

      // 해당 그룹의 아이템들만 추출
      const groupItems = items.filter((item) => item.istlDivsCd === groupKey);

      // 그룹 내에서 순서 변경
      const newGroupItems = moveItemInGroup(groupItems, dragInfo.item, targetItem);

      // 원본 데이터에서 순서 업데이트 (수정된 updateOriginalDataOrder 사용)
      const updatedOriginalData = updateOriginalDataOrder(items, groupKey, newGroupItems);

      if (!canReorder(updatedOriginalData, prevPrjSpCds)) {
        openMessageBar({
          type: 'warning',
          content: String(
            t(
              'setup.label.공통단계의 순서는 변경할 수 없습니다.',
              '공통단계의 순서는 변경할 수 없습니다.'
            )
          ),
        });
        return;
      }

      // 원본 데이터 업데이트
      setRowData((prev) => ({
        ...prev,
        [spTpCd]: updatedOriginalData,
      }));

      // CRUD 상태 업데이트 (기존 방식 유지)
      if (dragInfo.item.crudKey !== CrudCode.CREATE) {
        dragInfo.item.crudKey = CrudCode.UPDATE;
      }

      return;
    }
  });

  /**
   * 그룹(ROOT) 행을 제외한 전체 데이터 행 추출
   * 자동계산단계의 하위 항목도 합계 대상에 포함되어야 하므로 모든 depth를 순회함
   */
  const getDataItems = (items: any[], childItemsPath = 'children') => {
    const result: any[] = [];

    const traverse = (list: any[]) => {
      (list || []).forEach((item) => {
        if (item?.rowType !== 'ROOT') {
          result.push(item);
        }
        const children = item?.[childItemsPath];
        if (children?.length) {
          traverse(children);
        }
      });
    };

    traverse(items);

    return result;
  };

  const getCvcgScor = useEvent((item) => {
    if (!item || item.rowType === 'ROOT') return '';
    // 자동계산단계인 경우
    if (isAutoCalSp(item)) {
      return '';
    }
    if (readOnly) {
      // 읽기모드인 경우 조회된 값 노출
      return item.cvcgScor ?? '';
    }
    // 자동계산단계가 있는 경우: (자동계산 가중치 * [자동계산단계의 단계별 가중치]) / 100
    if (item.autoCalSpCd) {
      const autoSpCd = currentRows.find((o) => o.prjSpCd === item.autoCalSpCd);
      return (item.divsWghtScor * Number(autoSpCd?.wghtScor || 0)) / 100 || '';
    }
    // 자동계산단계가 없는 경우: 단계별 가중치
    return item.wghtScor || '';
  });

  /**
   * 셋업구분(ROOT) 그룹의 가중치 합계
   * 그룹에 속한 전체 행(자동계산단계 하위 행 포함)을 대상으로 합산
   */
  const getGroupScorSum = useEvent((item, binding) => {
    // 삭제 대상 행은 저장 시 검증 기준과 동일하게 합계에서 제외
    const groupItems = getDataItems(item?.children || []).filter(
      (o) => o.crudKey !== CrudCode.DELETE
    );
    if (groupItems.length < 1) return '';
    const sum = groupItems.reduce((acc, cur) => {
      const value = Number('cvcgScor' === binding ? getCvcgScor(cur) : cur[binding]);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    return sum || '';
  });

  /**
   * 자동계산단계(계산대상) 행의 하위 행 자동계산 가중치 합계
   * 하위 행의 자동계산 가중치 합계를 부모 행의 자동계산 가중치 위치에 노출하기 위함
   */
  const getSubDivsWghtScorSum = useEvent((item) => {
    if (!item || item.rowType === 'ROOT') return '';
    // 삭제 대상 행은 합계에서 제외
    const children = ((item.children || []) as any[]).filter((o) => o.crudKey !== CrudCode.DELETE);
    if (children.length < 1) return '';
    const sum = children.reduce((acc, cur) => {
      const value = Number(cur?.divsWghtScor);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    return sum || '';
  });

  const updateFooter = useEvent((grid) => {
    if (!grid) return;
    const dataItems = getDataItems(grid.collectionView.sourceCollection);
    if (dataItems.length < 1) {
      // 데이터가 없는 경우 합계 행 제거
      grid.columnFooters.rows.clear();
      return;
    }
    // 삭제 대상 행은 합계에서 제외
    const leafItems = dataItems.filter((o) => o.crudKey !== CrudCode.DELETE);
    if (grid.columnFooters.rows.length === 0) {
      // 합계 행 없는 경우 생성
      grid.columnFooters.rows.push(new GroupRow());
    }
    const footerRow = grid.columnFooters.rows[0];
    grid.columns.forEach((col) => {
      // 단계별 가중치, 환산 가중치
      if (['wghtScor', 'cvcgScor'].includes(col.binding)) {
        const sum = leafItems.reduce((total, item) => {
          const value = Number('cvcgScor' === col.binding ? getCvcgScor(item) : item[col.binding]);
          return total + (isNaN(value) ? 0 : value);
        }, 0);
        grid.columnFooters.setCellData(footerRow.index, col.index, sum);
      }
    });
  });

  return (
    <>
      <style>
        {`
          .wj-flexgrid div[wj-part=cf] div[wj-part=cfcells].wj-colfooters .wj-cell {
            border: 1px solid #DDE0DF;
            border-left: none;
            border-bottom: none;
            background: #F1F4F3 !important;
          }
          .wj-flexgrid div[wj-part=bl] div[wj-part=blcells].wj-bottomleft .wj-cell {
            border: 1px solid #DDE0DF;
            border-right: none;
            border-bottom: none;
            background: #F1F4F3 !important;
          }
          /* 공통단계 - DeepBlue100 */
          .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.cmnStepRow:not(.wj-state-multi-selected),
          .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.cmnStepRow:not(.wj-state-multi-selected):hover {
            background: #D8D9F9 !important;
          }
          .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.cmnStepRow {
            font-weight: 600;
          }
          /* 자동계산단계가 설정된 하위 행 - LightBlue100 */
          .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.autoCalRow:not(.wj-state-multi-selected),
          .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.autoCalRow:not(.wj-state-multi-selected):hover {
            background: #E3F0FF !important;
          }
        `}
      </style>
      <div css={tabs.wrap}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {spTpCds.map((o, idx) => (
            <Tab
              key={o.spTpCd}
              label={o.spTpNm}
              id={`setup-projectstepregist-setupstep-tab-${idx}`}
              aria-controls={`setup-projectstepregist-setupstep-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
        <div css={tabs.btn}>
          {!readOnly && (
            <>
              <label>
                <span>{t('setup.label.단계유형', '단계유형')}</span>
              </label>
              <ComboBox
                options={code?.prjSpDivsCd || []}
                placeholder={String(t('com.label.선택', '선택'))}
                defaultValue={istlDivsCd}
                onChange={(value) => {
                  setIstlDivsCd(value);
                }}
              />
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition?.elmPrjId)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </div>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={filterRowData}
          height={509}
          allowPinning={false}
          allowSorting={false}
          isFilter={false}
          isReadOnly={readOnly}
          isHoverCss={false}
          beginningEdit={onBeginningEdit}
          cellEditEnding={onCellEditEnding}
          cellEditEnded={onCellEditEnded}
          initialized={onInitialized}
          childItemsPath={['children', 'children']}
          loadedRows={onLoadedRows}
          draggingRow={(grid, e) => {
            const item = grid.rows[e.row].dataItem;
            dragIndex.current = {
              item,
              row: e.row,
              istlDivsCd: item.istlDivsCd,
            };
            grid.collectionView.moveCurrentToPosition(e.row);
          }}
          draggedRow={onDraggedRow}
        />
      </TabPanel>
      {!isReadOnly && (setgCpltYn === 'N' || !readOnly) && (
        <GlobalBtnGroup>
          {/* 단계설정이 완료되지 않은 경우에만 임시저장 가능 */}
          {setgCpltYn === 'N' && (
            <Button css={IconButton.button} className="draft" onClick={handleDraft}>
              {t('com.button.임시저장', '임시저장')}
            </Button>
          )}
          {/* 해당 단계유형에 설비가 등록되지 않은 경우에만 저장 가능 */}
          {!readOnly && (
            <Button css={IconButton.button} className="save" onClick={handleSave}>
              {t('com.button.저장', '저장')}
            </Button>
          )}
        </GlobalBtnGroup>
      )}

      {isOpenAddRowPopup && condition?.elmPrjId && spTpCds?.[currentTabIndex]?.spTpCd && (
        <SetupStepAddRowPopup
          open={isOpenAddRowPopup}
          close={() => setOpenAddRowPopup(false)}
          condition={{
            elmPrjId: condition?.elmPrjId,
            spTpCd: spTpCds?.[currentTabIndex]?.spTpCd,
          }}
          onCallback={(result) => {
            const spTpCd = spTpCds?.[currentTabIndex]?.spTpCd;
            if (rowData && spTpCd) {
              const uRowData = { ...rowData };
              const items = uRowData[spTpCd];
              const mapSteps = new Map(items.map((o) => [o.prjSpCd, o]));
              result.forEach((row) => {
                const existingRow = mapSteps.get(row.prjSpCd);
                if (existingRow) {
                  // 이미 존재하는 경우: 기존 값을 유지하면서 새 값으로 덮어쓰기 (Merge)
                  mapSteps.set(row.prjSpCd, {
                    ...existingRow,
                    ...row,
                  });
                } else {
                  // 신규행인 경우
                  mapSteps.set(row.prjSpCd, {
                    crudKey: CrudCode.CREATE,
                    spTpCd: spTpCd,
                    prjSpCd: row.prjSpCd,
                    prjSpNm: row.prjSpNm,
                    istlDivsCd: row.istlDivsCd,
                    cmnYn: 'N',
                    autoCalYn: undefined,
                    autoCalSpCd: row.autoCalSpCd,
                    divsWghtScor: row.divsWghtScor,
                    wghtScor: row.wghtScor,
                    cvcgScor: row.cvcgScor,
                  });
                }
              });
              const updatedItems = Array.from(mapSteps.values());
              setRowData((prev) => ({
                ...prev,
                [spTpCd]: updatedItems,
              }));
            }
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setup-projectstepregist-setupstep-tabpanel-${index}`}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}
class ProjectMergeManager extends CustomMergeManager {
  getMergedRange(panel, r, c, clip = true) {
    if (panel === panel.grid.columnFooters) {
      // 합계 행 단계별 가중치 앞까지 셀병합
      const endCol = panel.columns.indexOf(panel.columns.find((c) => c.binding === 'wghtScor'));
      if (c < endCol) {
        return new CellRange(r, 0, r, endCol - 1);
      }
    }
    return super.getMergedRange(panel, r, c, clip);
  }
}
export default React.forwardRef(SetupStepContent);
